import { useEffect, useState, useRef, useCallback } from "react";
import { connectSocket, AUTH_STORAGE_KEY, SERVER_URL } from "../../socket";

export default function GroupCallListener({ setVideoCall, setVoiceCall }) {
  const [incomingCall, setIncomingCall] = useState(null);

  const socketRef       = useRef(null);
  const incomingCallRef = useRef(null);
  const ringtoneRef     = useRef(null);
  const activeKeyRef    = useRef(null);   // ✅ tracks current active call key
  const isAcceptedRef   = useRef(false);  // ✅ prevent reject firing after accept

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  const playRingtone = useCallback(() => {
    try {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio("/ring.mp3");
        ringtoneRef.current.loop = true;
      }
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch(() => {});
    } catch {}
  }, []);

  const stopRingtone = useCallback(() => {
    try {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
    } catch {}
  }, []);

  const joinAllGroupRooms = useCallback(async (socket, token) => {
    let attempts = 0;

    const tryJoin = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/groups`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("fetch failed");

        const groups = await res.json();
        (groups || []).forEach(g => {
          socket.emit("join_group", String(g.id));
        });
        console.log(`📡 Joined ${(groups || []).length} group room(s)`);
      } catch (err) {
        attempts++;
        console.error(`joinGroupRooms attempt ${attempts}:`, err);
        // ✅ Retry up to 3 times
        if (attempts < 3) setTimeout(tryJoin, 2000);
      }
    };

    await tryJoin();
  }, []);

  useEffect(() => {
    let currentUser = null;
    let token       = null;

    try {
      const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      currentUser  = parsed?.user?.email;
      token        = parsed?.token;
    } catch {}

    if (!currentUser || !token) return;

    socketRef.current = connectSocket();
    const socket = socketRef.current;

    socket.emit("join", currentUser);
    joinAllGroupRooms(socket, token);

    // ✅ Rejoin rooms on reconnect
    const handleReconnect = () => {
      socket.emit("join", currentUser);
      joinAllGroupRooms(socket, token);
    };
    socket.on("connect", handleReconnect);

    const handleGroupIncoming = ({ groupId, groupName, caller, type }) => {
      // ✅ Never ring for yourself
      if (caller === currentUser) return;

      const key = `${groupId}_${caller}_${type}`;

      // ✅ Same call already showing — ignore duplicate socket event
      if (activeKeyRef.current === key) {
        console.log("⚠️ Duplicate ignored:", key);
        return;
      }

      // ✅ Another call already active — ignore
      if (incomingCallRef.current) {
        console.log("⚠️ Already showing a call, ignoring new one");
        return;
      }

      console.log(`📞 Group ${type} call: ${groupName} from ${caller}`);

      activeKeyRef.current  = key;
      isAcceptedRef.current = false; // ✅ reset for this new call

      setIncomingCall({ groupId, groupName, caller, type, key });
      playRingtone();
    };

    const handleGroupEnded = ({ groupId }) => {
      const current = incomingCallRef.current;
      if (!current) return;
      if (String(current.groupId) !== String(groupId)) return;

      // ✅ Don't dismiss if user already accepted
      if (isAcceptedRef.current) return;

      console.log("📴 Caller ended before answer");
      stopRingtone();
      activeKeyRef.current = null;
      setIncomingCall(null);
    };

    // ✅ Remove before adding — prevents double registration on hot reload
    socket.off("group_call_incoming", handleGroupIncoming);
    socket.off("group_call_ended",    handleGroupEnded);

    socket.on("group_call_incoming", handleGroupIncoming);
    socket.on("group_call_ended",    handleGroupEnded);

    return () => {
      socket.off("connect",             handleReconnect);
      socket.off("group_call_incoming", handleGroupIncoming);
      socket.off("group_call_ended",    handleGroupEnded);
      stopRingtone();
    };
  }, []);

  const acceptCall = useCallback(() => {
    const call = incomingCallRef.current;
    if (!call) return;

    // ✅ Mark accepted FIRST before any state changes
    isAcceptedRef.current = true;
    stopRingtone();

    const payload = {
      isGroup: true,
      group:   { id: call.groupId, name: call.groupName },
      type:    call.type
    };

    // ✅ Clear incoming BEFORE setting call to avoid flicker
    setIncomingCall(null);
    activeKeyRef.current = null;

    // ✅ Small delay so setIncomingCall(null) renders first
    setTimeout(() => {
      if (call.type === "video") {
        setVideoCall(payload);
      } else {
        setVoiceCall(payload);
      }
    }, 50);

  }, [stopRingtone, setVideoCall, setVoiceCall]);

  const rejectCall = useCallback(() => {
    // ✅ Guard — don't fire if already accepted
    if (isAcceptedRef.current) return;

    stopRingtone();
    activeKeyRef.current  = null;
    isAcceptedRef.current = false;
    setIncomingCall(null);
  }, [stopRingtone]);

  if (!incomingCall) return null;

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 9999
    }}>
      <div style={{
        background: "#fff", padding: 30,
        borderRadius: 12, textAlign: "center",
        minWidth: 320,
        boxShadow: "0 10px 40px rgba(0,0,0,0.3)"
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>
          {incomingCall.type === "video" ? "🎥" : "📞"}
        </div>

        <h2 style={{ margin: "0 0 8px" }}>
          Incoming Group {incomingCall.type === "video" ? "Video" : "Voice"} Call
        </h2>

        <p style={{ margin: "0 0 4px" }}>
          <b>{incomingCall.groupName}</b>
        </p>

        <p style={{ margin: "0 0 20px", color: "#999", fontSize: 14 }}>
          Started by {incomingCall.caller}
        </p>

        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={acceptCall}
            style={{
              padding: "12px 28px", background: "#22c55e",
              color: "#fff", border: "none",
              borderRadius: 8, fontSize: 16, cursor: "pointer"
            }}
          >
            ✅ Accept
          </button>

          <button
            onClick={rejectCall}
            style={{
              padding: "12px 28px", background: "#ef4444",
              color: "#fff", border: "none",
              borderRadius: 8, fontSize: 16, cursor: "pointer"
            }}
          >
            ❌ Decline
          </button>
        </div>
      </div>
    </div>
  );
}